import {
  readOfficePaymentChoice,
  toggleOfficePayment,
  type OfficePaymentChoice,
  type PaymentProblem,
} from "../../utils/paymentSplit";

const BUTTONS = [
  { key: "online", label: "Online" },
  { key: "cash", label: "Cash" },
  { key: "card", label: "Card" },
] as const;

/** The sentence for what the office payment still lacks. */
export function officePaymentProblemText(
  problem: PaymentProblem,
  totalCents: number,
  money: (eur: number) => string,
): string {
  if (problem === "pickMethod") return "Pick how it was paid.";
  if (problem === "nothingToSplit") return "Nothing to split — pick cash or card.";
  if (problem === "typeCash") return "Type how much was paid in cash.";
  return `Cash must be less than ${money(totalCents / 100)}.`;
}

/**
 * G-141 (OD-215): "How was it paid?" on the office "Add a sale". Office sales
 * are "usually fully paid online", so Online is on to start and stands alone
 * (a card payment link, a bank transfer, Bizum or the web checkout). Cash and
 * Card work as on the kiosk: one of them, or both with the cash part typed
 * and the card part the rest.
 */
export function OfficePaymentPicker({
  value,
  onChange,
  totalCents,
  money,
  disabled,
}: {
  value: OfficePaymentChoice;
  onChange: (next: OfficePaymentChoice) => void;
  totalCents: number;
  money: (eur: number) => string;
  disabled?: boolean;
}) {
  const reading = readOfficePaymentChoice(value, totalCents);
  const both = !value.online && value.cash && value.card;

  return (
    <div className="office-payment">
      <h3 className="office-payment-title">How was it paid?</h3>
      <div className="office-payment-buttons" role="group" aria-label="How was it paid?">
        {BUTTONS.map((b) => (
          <button
            key={b.key}
            type="button"
            aria-pressed={value[b.key]}
            className={value[b.key] ? "is-active" : ""}
            onClick={() => onChange(toggleOfficePayment(value, b.key))}
            disabled={disabled}
          >
            {b.label}
          </button>
        ))}
      </div>
      {both ? (
        <div className="payment-split">
          <label htmlFor="office-payment-cash">
            <span>Cash</span>
            <span className="payment-split-cash">
              €
              <input
                id="office-payment-cash"
                type="text"
                inputMode="decimal"
                value={value.cashStr}
                onChange={(event) => onChange({ ...value, cashStr: event.target.value })}
                aria-invalid={!reading.ok && reading.problem === "cashNotBelowTotal"}
                disabled={disabled}
              />
            </span>
          </label>
          <div>
            <span>Card — the rest</span>
            <strong className="payment-split-card">
              {reading.ok && reading.cardCents != null ? money(reading.cardCents / 100) : "—"}
            </strong>
          </div>
        </div>
      ) : null}
      {!reading.ok ? (
        <p
          className={
            reading.problem === "cashNotBelowTotal"
              ? "payment-split-problem is-error"
              : "payment-split-problem"
          }
          role={reading.problem === "cashNotBelowTotal" ? "alert" : undefined}
        >
          {officePaymentProblemText(reading.problem, totalCents, money)}
        </p>
      ) : null}
    </div>
  );
}
